import React, { useEffect, useState, useMemo } from 'react';
import { Plus, Trash2, Edit3, GripVertical, MoveVertical, Save, X, Award, Users } from 'lucide-react';
import { api, formatApiError } from '../../lib/api';
import { useReorder } from '../../lib/useReorder';

const EMPTY = { name: '', period: '', note: '', is_malintzin: false, award_year: '' };

export default function AdminPastPresidentsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('presidents'); // presidents | malintzin

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get('/past-presidents');
      setItems(data || []);
    } catch { setItems([]); }
    setLoading(false);
  }
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => items.filter((x) => tab === 'malintzin' ? x.is_malintzin : !x.is_malintzin), [items, tab]);
  const dnd = useReorder(filtered, (it) => it.id);
  const visible = dnd.reorderMode ? dnd.orderDraft.map((id) => filtered.find((i) => i.id === id)).filter(Boolean) : filtered;

  async function persistOrder(ids) {
    try {
      // Send only the filtered set's order; backend updates only matched IDs.
      await api.put('/admin/past-presidents/reorder', { ids });
      await load();
    } catch (err) { alert(formatApiError(err.response?.data?.detail)); }
  }

  function startEdit(it) {
    setEditingId(it.id);
    setForm({ name: it.name || '', period: it.period || '', note: it.note || '', is_malintzin: !!it.is_malintzin, award_year: it.award_year || '' });
    setShowForm(true); setError('');
  }
  function startCreate() {
    setEditingId(null);
    setForm({ ...EMPTY, is_malintzin: tab === 'malintzin' });
    setShowForm(true); setError('');
  }
  function cancelForm() {
    setShowForm(false); setEditingId(null); setForm(EMPTY); setError('');
  }

  async function save() {
    setSaving(true); setError('');
    try {
      if (editingId) await api.put(`/admin/past-presidents/${editingId}`, form);
      else await api.post('/admin/past-presidents', form);
      await load(); cancelForm();
    } catch (err) { setError(formatApiError(err.response?.data?.detail)); }
    setSaving(false);
  }
  async function remove(id) {
    if (!window.confirm('¿Eliminar este registro?')) return;
    try { await api.delete(`/admin/past-presidents/${id}`); await load(); }
    catch (err) { alert(formatApiError(err.response?.data?.detail)); }
  }

  return (
    <div data-testid="admin-past-presidents-page" className="space-y-6">
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-charcoal-800 flex items-center gap-2"><Award size={22} />Expresidentes y Premio Malintzin</h1>
          <p className="text-charcoal-500 text-sm mt-1">{items.filter((x) => !x.is_malintzin).length} expresidentes · {items.filter((x) => x.is_malintzin).length} galardonados</p>
        </div>
        <div className="flex items-center gap-2">
          {dnd.reorderMode ? (
            <>
              <button onClick={() => dnd.saveOrder(persistOrder)} disabled={dnd.saving} data-testid="past-save-order-btn" className="inline-flex items-center gap-2 bg-emerald-600 text-white rounded-lg px-3 py-2 text-sm font-medium hover:bg-emerald-700">
                <Save size={14} />Guardar orden
              </button>
              <button onClick={dnd.cancelReorder} className="inline-flex items-center gap-2 bg-white border border-gray-200 text-charcoal-700 rounded-lg px-3 py-2 text-sm font-medium hover:bg-gray-50">
                <X size={14} />Cancelar
              </button>
            </>
          ) : (
            <>
              <button onClick={dnd.enterReorder} data-testid="past-reorder-btn" className="inline-flex items-center gap-2 bg-white border border-gray-200 text-charcoal-700 rounded-lg px-3 py-2 text-sm font-medium hover:bg-gray-50">
                <MoveVertical size={14} />Reordenar
              </button>
              <button onClick={startCreate} data-testid="past-create-btn" className="inline-flex items-center gap-2 bg-cmic-600 text-white rounded-lg px-3 py-2 text-sm font-medium hover:bg-cmic-700">
                <Plus size={14} />Añadir
              </button>
            </>
          )}
        </div>
      </header>

      <div className="flex border-b border-gray-200">
        <button onClick={() => { setTab('presidents'); dnd.cancelReorder(); }} data-testid="past-tab-presidents" className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 ${tab === 'presidents' ? 'text-cmic-600 border-cmic-500' : 'text-charcoal-500 border-transparent hover:text-charcoal-700'}`}>
          <Users size={14} />Expresidentes
        </button>
        <button onClick={() => { setTab('malintzin'); dnd.cancelReorder(); }} data-testid="past-tab-malintzin" className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 ${tab === 'malintzin' ? 'text-cmic-600 border-cmic-500' : 'text-charcoal-500 border-transparent hover:text-charcoal-700'}`}>
          <Award size={14} />Premio Malintzin
        </button>
      </div>

      {dnd.reorderMode && <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-800">Arrastra los elementos con ⋮⋮ para reordenarlos.</div>}

      {showForm && (
        <div className="bg-white border border-cmic-200 rounded-2xl p-6 shadow-sm space-y-4" data-testid="past-form">
          <h3 className="font-semibold text-charcoal-800">{editingId ? 'Editar registro' : (form.is_malintzin ? 'Nuevo galardonado' : 'Nuevo expresidente')}</h3>
          <label className="inline-flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.is_malintzin} onChange={(e) => setForm({ ...form, is_malintzin: e.target.checked })} data-testid="past-form-malintzin" /> Premio Malintzin
          </label>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-charcoal-600 mb-1.5">Nombre</label>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="past-form-name" className="w-full rounded-xl border border-gray-200 px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-cmic-500/30 focus:border-cmic-500" />
            </div>
            {form.is_malintzin ? (
              <div>
                <label className="block text-xs font-medium text-charcoal-600 mb-1.5">Año del premio</label>
                <input value={form.award_year} onChange={(e) => setForm({ ...form, award_year: e.target.value })} placeholder="Ej: 2024" data-testid="past-form-year" className="w-full rounded-xl border border-gray-200 px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-cmic-500/30 focus:border-cmic-500" />
              </div>
            ) : (
              <div>
                <label className="block text-xs font-medium text-charcoal-600 mb-1.5">Periodo</label>
                <input value={form.period} onChange={(e) => setForm({ ...form, period: e.target.value })} placeholder="Ej: 2022-2025" data-testid="past-form-period" className="w-full rounded-xl border border-gray-200 px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-cmic-500/30 focus:border-cmic-500" />
              </div>
            )}
          </div>
          <div>
            <label className="block text-xs font-medium text-charcoal-600 mb-1.5">Nota (opcional)</label>
            <input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Ej: qepd, in memoriam" data-testid="past-form-note" className="w-full rounded-xl border border-gray-200 px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-cmic-500/30 focus:border-cmic-500" />
          </div>
          {error && <div className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-lg p-2.5">{error}</div>}
          <div className="flex gap-2 pt-2">
            <button onClick={save} disabled={saving || !form.name.trim()} data-testid="past-form-save" className="inline-flex items-center gap-2 bg-cmic-600 text-white rounded-lg px-4 py-2 text-sm font-medium hover:bg-cmic-700 disabled:opacity-50">
              <Save size={14} />{saving ? 'Guardando…' : 'Guardar'}
            </button>
            <button onClick={cancelForm} className="inline-flex items-center gap-2 bg-white border border-gray-200 text-charcoal-700 rounded-lg px-4 py-2 text-sm font-medium hover:bg-gray-50">Cancelar</button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-16"><div className="w-8 h-8 border-3 border-cmic-200 border-t-cmic-500 rounded-full animate-spin" /></div>
      ) : visible.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-2xl p-12 text-center text-charcoal-500">Sin registros aún.</div>
      ) : (
        <ul className="space-y-2">
          {visible.map((it) => (
            <li key={it.id} draggable={dnd.reorderMode} {...(dnd.reorderMode ? dnd.dragProps(it.id) : {})} data-testid={`past-row-${it.id}`} className={`bg-white rounded-xl border p-3 flex items-center gap-3 ${dnd.reorderMode ? 'cursor-move border-amber-200 bg-amber-50/50' : 'border-gray-200 hover:border-cmic-300 transition-all'}`}>
              {dnd.reorderMode && <GripVertical size={14} className="text-charcoal-400 flex-shrink-0" />}
              <div className={`w-2 h-2 rounded-full flex-shrink-0 ${it.is_malintzin ? 'bg-amber-500' : 'bg-cmic-500'}`} />
              <div className="flex-1 min-w-0">
                <span className="text-sm font-medium text-charcoal-800">{it.name}</span>
                {it.note && <span className="ml-2 text-xs text-charcoal-400 italic">({it.note})</span>}
              </div>
              <span className="text-sm tabular-nums text-charcoal-600 flex-shrink-0">{it.is_malintzin ? it.award_year : it.period}</span>
              {!dnd.reorderMode && (
                <div className="flex gap-1 flex-shrink-0">
                  <button onClick={() => startEdit(it)} title="Editar" data-testid={`past-edit-${it.id}`} className="p-1.5 rounded text-charcoal-500 hover:bg-charcoal-50"><Edit3 size={12} /></button>
                  <button onClick={() => remove(it.id)} title="Eliminar" data-testid={`past-delete-${it.id}`} className="p-1.5 rounded text-rose-500 hover:bg-rose-50"><Trash2 size={12} /></button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
